import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useOutletContext } from "react-router-dom";
import moment from 'moment';
import BaseTimeseriesAPIChart from "@/components/DashboardCard/BaseTimeseriesAPIChart";
import DropdownScrollGroup from '@/components/DropdownScrollGroup';
import withProject from '@/withProject';
import StaticLayoutInner from '@/layouts/StaticLayoutInner';
import StatsCard from '@/components/DashboardCard/StatsCard';
import { fetchAPI } from "@/api";

function Dashboard() {
    const periodOptions = [
        { id: 0, title: "Last 7 days", date: { number: 7, period: 'days' } },
        { id: 1, title: "Last 4 weeks", date: { number: 4, period: 'weeks' } },
        { id: 2, title: "Last 3 months", date: { number: 3, period: 'months' } },
        { id: 3, title: "Last 12 months", date: { number: 12, period: 'months' } },
        { id: 4, title: "Month to date", date: { number: moment().diff(moment().startOf('month'), 'days'), period: 'days', } },
        { id: 5, title: "Quarter to date", date: { number: moment().diff(moment().startOf('quarter'), 'days'), period: 'days', } },
        { id: 6, title: "Year to date", date: { number: moment().diff(moment().startOf('year'), 'days'), period: 'days', } },
        { id: 7, title: "All time", date: { number: 0, period: 'days' } },
        // { id: 8, title: "Custom" },
    ];

    const comparedToOptionsDefault = [
        { id: 0, title: "Previous period" },
        { id: 1, title: "Previous month" },
        { id: 2, title: "Previous quarter" },
        { id: 3, title: "Previous year" },
        { id: 4, title: "No comparison" },
    ];
    const unitOptionsDefault = [
        { id: 0, title: "Daily", value: "day" },
        { id: 1, title: "Weekly", value: "week" },
        { id: 2, title: "Monthly", value: "month" },
    ];

    const buildPeriods = ({ id, date }) => {
        // let tomorrow = moment().utc().startOf('day').add(1, 'day');
        // let start, end = tomorrow;
        let tomorrow = moment().utc().startOf('day');
        let start = tomorrow;
        let end = tomorrow.clone().add(1, 'day');

        if (id === 4) {
            if (timeframe === 'week') {
                start = tomorrow.clone().startOf('month').startOf('isoWeek');
            } else {
                start = tomorrow.clone().startOf('month');
            }
        } else if (id === 5) {
            if (timeframe === 'week') {
                start = tomorrow.clone().startOf('quarter').startOf('isoWeek');
            } else {
                start = tomorrow.clone().startOf('quarter');
            }
        } else if (id === 6) {
            if (timeframe === 'week') {
                // Round date if there is less than 4 days in week from the beginning of the year
                start = tomorrow.clone().startOf('year');
                const jan1 = start.clone();
                const jan1DayOfWeek = jan1.isoWeekday();

                if (jan1DayOfWeek > 3) {
                    start = jan1.startOf('isoWeek');
                }
            } else {
                start = tomorrow.clone().startOf('year');
            }
        } else if (id === 7) {
            start = moment(project?.createdAt, 'YYYY-MM-DD') || new Date();
        } else {
            start = tomorrow.clone().subtract(date.number, date.period);
            if (timeframe === 'week') {
                start = start.clone().startOf('isoWeek').add(1, 'week');
            } else if (timeframe === 'month') {
                start = start.clone().startOf('month').add(1, 'month');
            }
        }

        let newPeriod = { start: start.valueOf(), end: end.valueOf() };
        let newPrevPeriod = { start:  start - (end - start), end: start.valueOf() };

        return [newPeriod, newPrevPeriod];
    }
    
    const buildPrevPeriods = (item) => {
        let start = moment(period.start);
        let end = moment(period.end);

        if (item.id === 0) {
            let diff = end.diff(start, 'day');

            if (timeframe === 'week') {
                start = moment(period.start).subtract(diff, 'day').startOf('isoWeek');
                end = moment(period.start).subtract(1, 'day');
            } else if (timeframe === 'month') {
                start = moment(period.start).subtract(diff, 'day').startOf('month');
                end = moment(period.start).subtract(1, 'day');
            } else {
                start = moment(start.valueOf() - (end.valueOf() - start.valueOf()));
                end = moment(period.start);
            }
        } else if (item.id === 1) {
            if (timeframe === 'week') {
                start = moment(period.start).subtract(4, 'week').startOf('isoWeek');
                end = moment(period.end).subtract(4, 'week').endOf('isoWeek');
            } else if (timeframe === 'month') {
                start = moment(period.start).subtract(1, 'month').startOf('month');
                end = moment(period.end).subtract(1, 'month').endOf('month');
            } else {
                start = moment(moment(period.end).subtract(1, 'month').valueOf() - (period.end - period.start));
                end = moment(period.end).subtract(1, 'month');
            }
        } else if (item.id === 2) {
            if (timeframe === 'week') {
                start = moment(start).subtract(12, 'week').startOf('isoWeek');
                end = moment(end).subtract(12, 'week').endOf('isoWeek');
            } else if (timeframe === 'month') {
                start = moment(period.start).subtract(3, 'month').startOf('month');
                end = moment(period.end).subtract(3, 'month').endOf('month');
            } else {
                start = moment(moment(period.end).subtract(3, 'month').valueOf() - (period.end - period.start));
                end = moment(period.end).subtract(3, 'month');
            }
        } else if (item.id === 3) {
            if (timeframe === 'week') {
                let weeksInYear = moment(start).isoWeeksInYear();

                start = moment(start).subtract(weeksInYear, 'week').startOf('isoWeek');
                end = moment(end).subtract(weeksInYear, 'week').endOf('isoWeek');
            } else if (timeframe === 'month') {
                start = moment(period.start).subtract(1, 'year').startOf('month');
                end = moment(period.end).subtract(1, 'year').endOf('month');
            } else {
                start = moment(moment(period.end).subtract(1, 'year').valueOf() - (period.end - period.start));
                end = moment(period.end).subtract(1, 'year');
            }
        }

        start = start.valueOf();
        end = end.valueOf();

        return {
            ...item,
            date: {
                start,
                end,
            },
        }
    }

    const buildUnitOptions = (item) => {
        const daysDiff = moment.utc(period.end).diff(moment.utc(period.start), 'days');
        const maxPoints = 100;

        const visibilityRules = {
            day: daysDiff >= 2 && daysDiff <= maxPoints,
            week: daysDiff / 7 >= 2 && daysDiff / 7 <= maxPoints,
            month: daysDiff / 30 >= 2 && daysDiff / 30 <= maxPoints,
        };

        return {
            ...item,
            visible: visibilityRules[item.value],
        };
    }

    const { project, auth } = useOutletContext();

    const [filter, setFilter] = useState({});
    const [timeframe, setTimeframe] = useState("day"); // hour/day/week/month/year
    let [defaultPeriod, defaultPrevPeriod] = buildPeriods(periodOptions[1]);
    const [period, setPeriod] = useState(defaultPeriod);
    const [prevPeriod, setPrevPeriod] = useState(defaultPrevPeriod);

    const [comparedToOptions, setComparedToOptions] = useState(comparedToOptionsDefault);
    const [comparedToVisible, setComparedToVisible] = useState(true);
    const [comparedToDatepickerVisible, setComparedToDatepickerVisible] = useState(false);
    // const [comparedCustom, setComparedCustom] = useState(prevPeriod);
    const [unitOptions, setUnitOptions] = useState(unitOptionsDefault);

    const [periodState, setPeriodState] = useState({
        timeframe: "day",
        period: defaultPeriod,
        prevPeriod: defaultPrevPeriod,
        filter,
    });

    const dropdownsRef = useRef();

    const [chartsData, setChartsData] = useState({
        botUsers: { isLoading: true, rows: null, prevRows: null, error: null },
        usdVolume: { isLoading: true, rows: null, prevRows: null, error: null },
        payments: { isLoading: true, rows: null, prevRows: null, error: null },
        recurringPayments: { isLoading: true, rows: null, prevRows: null, error: null }
    });

    function buildDayValueTableWithMissingDays(data, periodStart, periodEnd) {
        const startDate = new Date(periodStart);
        const endDate = new Date(periodEnd);

        const start = moment(startDate);
        const end = moment(endDate).subtract(1, 'day');

        const allDates = [];
        const dataMap = new Map(data.map(obj => [moment(obj.day).format('Y-MM-DD'), obj.value]));

        for (let currentDate = start.clone(); currentDate.isSameOrBefore(end); currentDate.add(1, 'day')) {
            allDates.push(currentDate.format('YYYY-MM-DD'));
        }

        const results = allDates.map((day) => {
            const value = dataMap.get(day) || 0;
            return {
                day: day,
                value: value
            };
        });

        return results;
    }

    function buildWeekly(data, periodStart, periodEnd) {
        const startDate = new Date(periodStart);
        const endDate = new Date(periodEnd);

        const start = moment(startDate).startOf('isoWeek');
        const end = moment(endDate).endOf('isoWeek');

        const dataMap = new Map();

        data.forEach(obj => {
            const week = obj.day;
            const existingValue = dataMap.get(week) || 0;
            dataMap.set(week, existingValue + obj.value);
        });

        const allWeeks = [];
        for (let currentDate = start.clone(); currentDate.isSameOrBefore(end); currentDate.add(1, 'week')) {
            const week = currentDate.isoWeekYear() * 100 + currentDate.isoWeek();
            allWeeks.push(week);
        }

        return allWeeks.map((week) => {
            return {
                day: week,
                value: dataMap.get(week) || 0
            };
        });
    }

    function buildMonthly(data, periodStart, periodEnd) {
        const startDate = new Date(periodStart);
        const endDate = new Date(periodEnd);

        const start = moment(startDate);
        const end = moment(endDate).endOf('month');

        const allMonths = [];
        const dataMap = new Map(data.map(obj => [Number(obj.day), obj.value]));

        for (let currentDate = start.clone(); currentDate.isSameOrBefore(end); currentDate.add(1, 'month')) {
            const month = currentDate.format('YYYYMM');
            allMonths.push(Number(month));
        }

        return allMonths.map((month) => {
            return {
                day: month,
                value: dataMap.get(month) || 0
            };
        });
    }

    const fetchData = useCallback(async (chartKey, apiMethod, controller) => {
        let prevPeriodStart = periodState.prevPeriod.start;
        let prevPeriodEnd = periodState.prevPeriod.end;
        let periodStart = periodState.period.start;
        let periodEnd = periodState.period.end;
        let periodSelected = dropdownsRef.current && dropdownsRef.current.getSelectedPeriod();
        let comparedToSelected = dropdownsRef.current && dropdownsRef.current.getSelectedCompared();
        let hidePrev = (periodSelected && periodSelected.title === 'All time' || comparedToSelected && comparedToSelected.title === 'No comparison') || false;

        try {
            setChartsData(prev => ({
                ...prev,
                [chartKey]: { ...prev[chartKey], isLoading: true, error: null }
            }));

            let params = {
                projectId: project.projectId,
                periodStart,
                periodEnd,
                timeframe,
                filter,
            };

            if (periodStart !== prevPeriodStart && periodEnd !== prevPeriodEnd) {
                params = {
                    ...params,
                    prevPeriodStart,
                    prevPeriodEnd,
                }
            }

            const response = await fetchAPI(apiMethod, auth.user.token, params, { signal: controller.signal });
            if (!response.ok) {
                throw new Error(`This is an HTTP error: The status is ${response.status}`);
            }
            let actualData = await response.json();

            let rows, prevRows;
            
            if (timeframe === 'day') {
                rows = buildDayValueTableWithMissingDays(actualData.rows, periodStart, periodEnd);
                prevRows = !hidePrev ? buildDayValueTableWithMissingDays(actualData.prevRows, prevPeriodStart, prevPeriodEnd) : null;
            } else if (timeframe === 'week') {
                rows = buildWeekly(actualData.rows, periodStart, periodEnd);
                prevRows = !hidePrev ? buildWeekly(actualData.prevRows, prevPeriodStart, prevPeriodEnd) : null;
            } else if (timeframe === 'month') {
                rows = buildMonthly(actualData.rows, periodStart, periodEnd);
                prevRows = !hidePrev ? buildMonthly(actualData.prevRows, prevPeriodStart, prevPeriodEnd) : null;
            } else {
                rows = actualData.rows;
                prevRows = !hidePrev ? actualData.prevRows : null;
            }

            setChartsData(prev => ({
                ...prev,
                [chartKey]: {
                    isLoading: false,
                    rows,
                    prevRows,
                    error: null,
                }
            }));
        } catch (error) {
            let errorMessage = 'An unexpected error occurred';
            if (error.response?.message && error.name !== "AbortError") {
                errorMessage = error.response.message;
            }
            setChartsData(prev => ({
                ...prev,
                [chartKey]: {
                    ...prev[chartKey],
                    isLoading: false,
                    error: errorMessage
                }
            }));
        }
    });

    const calculateTotals = (data) => {
        if (!data || !data.rows) return {};
        
        let totalValue = data.rows.map(r => r.value).reduce((a, b) => a + b, 0);
        let prevTotalValue, relativeChange, relativeChangeString;

        if (data.prevRows && data.prevRows.length) {
            prevTotalValue = data.prevRows.map(r => r.value).reduce((a, b) => a + b, 0);
            
            if (prevTotalValue > 0 || prevTotalValue < 0) {
                relativeChange = (totalValue - prevTotalValue) / prevTotalValue;
            } else if (prevTotalValue === 0 && totalValue === prevTotalValue) {
                relativeChange = 0;
            } else if (prevTotalValue === 0) {
                relativeChange = totalValue > 0 ? 1 : -1;
            }

            relativeChangeString = (relativeChange >= 0 ? '+' : '') + (relativeChange * 100).toFixed(1) + '%';
        }

        return {
            totalValue,
            prevTotalValue,
            relativeChange,
            relativeChangeString
        };
    };

    const onPeriodChange = (value) => {
        let [newPeriod, newPrevPeriod] = buildPeriods(value);

        if (value.date.period === "days" && value.date.number === 7) {
            setTimeframe("day");
        }

        setPeriod(newPeriod);

        const selected = periodOptions.filter((item) => item.id === value.id)[0];

        if (selected && selected.title === 'All time') {
            setComparedToVisible(false);
        } else {
            setComparedToVisible(true);
        }
    }

    const onComparedToChange = (value) => {
        const selected = comparedToOptions.filter((item) => item.id === value.id)[0];

        if (selected && selected.title === 'Custom') {
            setComparedToDatepickerVisible(true);
        } else {
            setComparedToDatepickerVisible(false);
        }
        if (value.date) {
            setPrevPeriod(value.date);
        }
    }

    const onUnitChange = ({ value }) => {
        setTimeframe(value);
    }

    const onFilterChange = (value) => {
        setFilter(value);
    }

    useEffect(() => {
        const controller = new AbortController()

        fetchData('botUsers', '/project/charts/newBotUsers', controller);
        fetchData('usdVolume', '/project/charts/usdVolume', controller);
        fetchData('payments', '/project/charts/payments', controller);
        fetchData('recurringPayments', '/project/charts/recurringPayments', controller);

        return () => {
            controller.abort();
        };
    }, [project?.projectId, JSON.stringify(periodState)]);

    useEffect(() => {
        setComparedToOptions(comparedToOptions.map((item) => buildPrevPeriods(item)));

        if (JSON.stringify(unitOptions.map((item) => buildUnitOptions(item))) !== JSON.stringify(unitOptions)) {
            setUnitOptions(unitOptions.map((item) => buildUnitOptions(item)));
        }
    }, [period]);

    useEffect(() => {
        let comparedToSelected = dropdownsRef.current.getSelectedCompared();
        const periodSelected = dropdownsRef.current.getSelectedPeriod();
        const unitSelected = dropdownsRef.current.getSelectedUnit();
        if (periodSelected && periodSelected.title === "All time") {
            // comparedToSelected = comparedToOptions.filter((item) => item.title === "No comparison")[0];
        }

        if ((comparedToSelected && comparedToSelected.date) && ((comparedToSelected.date.start !== prevPeriod.start) || (comparedToSelected.date.end !== prevPeriod.end))) {
            setPrevPeriod(comparedToSelected.date);
            setTimeframe(unitSelected.value);
        }
    }, [comparedToOptions]);

    useEffect(() => {
        const periodSelected = dropdownsRef.current.getSelectedPeriod();
        let [newPeriod, newPrevPeriod] = buildPeriods(periodSelected);

        if (period.start !== newPeriod.start || period.end !== newPeriod.end) {
            setPeriod(newPeriod);
        }

    }, [timeframe]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setPeriodState({
                timeframe,
                period,
                prevPeriod,
                filter,
            });
        }, 100);

        return () => {
            clearTimeout(timeout);
        };
    }, [timeframe, period, prevPeriod, filter]);

    return (
        <StaticLayoutInner noPadding>
            <div className="py-4 sm:py-10 px-4 xl:px-[72px]">
                <div className="sm:flex sm:justify-between sm:items-center">
                    <DropdownScrollGroup ref={dropdownsRef}
                                            periodOptions={periodOptions}
                                            comparedToOptions={comparedToOptions}
                                            unitOptions={unitOptions}
                                            onPeriodChange={onPeriodChange}
                                            onComparedToChange={onComparedToChange}
                                            onUnitChange={onUnitChange}
                                            onFilterChange={onFilterChange}
                                            period={periodState.period}
                                            comparedToVisible={comparedToVisible}
                                            comparedToDatepickerVisible={comparedToDatepickerVisible} />
                </div>

                <div className="grid grid-cols-12 gap-4 mb-12">
                    <StatsCard
                        title="Bot users"
                        isLoading={chartsData.botUsers.isLoading}
                        data={calculateTotals(chartsData.botUsers)}
                    />
                    <StatsCard
                        title="Volume from sales"
                        isLoading={chartsData.usdVolume.isLoading}
                        data={calculateTotals(chartsData.usdVolume)}
                        currency="USD"
                    />
                    <StatsCard
                        title="One-time payments"
                        isLoading={chartsData.payments.isLoading}
                        data={calculateTotals(chartsData.payments)}
                    />
                    <StatsCard
                        title="Recurring payments"
                        isLoading={chartsData.recurringPayments.isLoading}
                        data={calculateTotals(chartsData.recurringPayments)}
                    />
                </div>

                <div className="flex items-center flex-wrap gap-x-6 gap-y-4 mb-6 text-[12px] font-semibold leading-[1.1]">
                    <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-[#3CBBCE]" />
                        <span>{dropdownsRef.current?.getSelectedPeriod()?.title}</span>
                    </div>
                    {comparedToVisible && dropdownsRef.current?.getSelectedCompared()?.title !== 'No comparison' && (
                        <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full bg-[#9D8DD0]" />
                            <span>{dropdownsRef.current?.getSelectedCompared()?.title}</span>
                        </div>
                    )}
                </div>

                {/* Charts */}
                <div className="grid grid-cols-12 gap-4 mb-10">
                    <BaseTimeseriesAPIChart
                        title="Bot users"
                        timeframe={periodState.timeframe}
                        data={chartsData.botUsers}
                        periodStart={periodState.period.start}
                        periodEnd={periodState.period.end}
                        periodSelected={dropdownsRef.current && dropdownsRef.current.getSelectedPeriod()}
                    />
                    <BaseTimeseriesAPIChart
                        title="Volume from sales"
                        timeframe={periodState.timeframe}
                        data={chartsData.usdVolume}
                        periodStart={periodState.period.start}
                        periodEnd={periodState.period.end}
                        periodSelected={dropdownsRef.current && dropdownsRef.current.getSelectedPeriod()}
                        currency="USD"
                    />
                    <BaseTimeseriesAPIChart
                        title="One-time payments"
                        timeframe={periodState.timeframe}
                        data={chartsData.payments}
                        periodStart={periodState.period.start}
                        periodEnd={periodState.period.end}
                        periodSelected={dropdownsRef.current && dropdownsRef.current.getSelectedPeriod()}
                    />
                    <BaseTimeseriesAPIChart
                        title="Recurring payments"
                        timeframe={periodState.timeframe}
                        data={chartsData.recurringPayments}
                        periodStart={periodState.period.start}
                        periodEnd={periodState.period.end}
                        periodSelected={dropdownsRef.current && dropdownsRef.current.getSelectedPeriod()}
                    />

                    {/*<BaseTimeseriesAPIChart*/}
                    {/*    title="One-time payments USD Volume" apiMethod="/project/charts/paymentsUSDVolume"*/}
                    {/*    periodStart={period.start} periodEnd={period.end} prevPeriodStart={prevPeriod.start}*/}
                    {/*    prevPeriodEnd={prevPeriod.end} timeframe={timeframe}*/}
                    {/*/>*/}

                    {/*<BaseTimeseriesAPIChart*/}
                    {/*    title="Recurring payments USD Volume" apiMethod="/project/charts/recurringPaymentsUSDVolume"*/}
                    {/*    periodStart={period.start} periodEnd={period.end} prevPeriodStart={prevPeriod.start}*/}
                    {/*    prevPeriodEnd={prevPeriod.end} timeframe={timeframe}*/}
                    {/*/>*/}
                </div>
            </div>
        </StaticLayoutInner>
    );
}

export default withProject(Dashboard);
